import { useState, type FormEvent } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@weco/ui";
import { useUpdateAssistantProfile } from "../../../modules/assistant/hooks/useAssistantProfile";
import { useAssistantConfig } from "../useAssistantConfig";
import { HueSwatches } from "./HueSwatches";

const COMPANION_NAME_MAX = 24;

export function Customise({ onDone }: { onDone: () => void }) {
  const cfg = useAssistantConfig();
  const updateProfile = useUpdateAssistantProfile();

  const [name, setName] = useState(cfg.assistant_name ?? "");
  const [hue, setHue] = useState(cfg.avatar_hue);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pickHue = (nextHue: number) => {
    setHue(nextHue);
    // Optimistic recolour: PATCH immediately, same as the kit's `repaint`.
    updateProfile.mutate({ avatar_hue: nextHue });
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const trimmed = name.trim();
    setError(null);

    if (!trimmed || trimmed === cfg.assistant_name) {
      onDone();
      return;
    }

    setSaving(true);
    try {
      await updateProfile.mutateAsync({ assistant_name: trimmed });
      onDone();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "That didn't save. Try again?");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-3 p-4">
      <div>
        <input
          className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground outline-none focus:ring-1 focus:ring-border"
          value={name}
          maxLength={COMPANION_NAME_MAX}
          onChange={(event) => setName(event.target.value)}
          placeholder="e.g. Pixel"
          aria-invalid={error ? true : undefined}
        />
        {error ? (
          <p className="mt-2 text-sm text-rose-600" role="alert">
            {error}
          </p>
        ) : null}
      </div>

      <HueSwatches value={hue} onPick={pickHue} />

      <button
        type="submit"
        disabled={saving}
        className={cn(
          "flex items-center justify-center gap-2 self-end rounded-full bg-muted px-4 py-2 text-sm font-semibold text-foreground ring-1 ring-inset ring-border transition-colors hover:bg-muted/70",
          "disabled:opacity-60",
        )}
      >
        {saving ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
        Save
      </button>
    </form>
  );
}
